/**
 * Shared constants for the Social Media Analytics section.
 */

/** Platforms the sync actually supports. Not a free-text field anywhere. */
export const PLATFORMS = ['Facebook', 'Instagram', 'YouTube'] as const;
export type Platform = (typeof PLATFORMS)[number];

export const PLATFORM_COLOR: Record<Platform, string> = {
  Facebook: 'blue',
  Instagram: 'pink',
  YouTube: 'red',
};

/** URL segment <-> platform name, for /platform/:platform routes. */
export const PLATFORM_SLUG: Record<Platform, string> = {
  Facebook: 'facebook',
  Instagram: 'instagram',
  YouTube: 'youtube',
};

export function platformFromSlug(slug?: string): Platform | undefined {
  if (!slug) return undefined;
  return PLATFORMS.find((p) => PLATFORM_SLUG[p] === slug.toLowerCase());
}

/**
 * Date-range presets.
 *
 * Presets are stored as the preset KEY, not as the resolved dates, so "Last 28 days"
 * stays rolling across reloads instead of freezing to whichever 28 days it was when the
 * filter was saved.
 */
export const DATE_PRESETS = [
  { value: 'last7', label: 'Last 7 days', days: 7 },
  { value: 'last28', label: 'Last 28 days', days: 28 },
  { value: 'last90', label: 'Last 90 days', days: 90 },
  { value: 'thisMonth', label: 'This month', days: 0 },
  { value: 'lastMonth', label: 'Last month', days: 0 },
  { value: 'ytd', label: 'Year to date', days: 0 },
  { value: 'custom', label: 'Custom range', days: 0 },
] as const;

export type DatePreset = (typeof DATE_PRESETS)[number]['value'];

export const GRANULARITIES = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
] as const;

export type Granularity = (typeof GRANULARITIES)[number]['value'];

/** Keys for the persisted per-page filter store (see stores/pageFiltersStore). */
export const FILTER_PAGE_KEYS = {
  DASHBOARD: 'social-media/dashboard',
  PLATFORM: 'social-media/platform',
  CONTENT: 'social-media/content',
  VIDEO: 'social-media/video',
  AUDIENCE: 'social-media/audience',
  ADS: 'social-media/ads',
  REPORTS: 'social-media/reports',
} as const;

/** One height for every chart card, so the grid lines up. */
export const CHART_HEIGHT = 300;

/**
 * Metrics Meta retired, and what replaced them.
 *
 * Surfaced in the UI so a blank Facebook audience panel reads as "Meta removed this"
 * rather than "the dashboard is broken". See the worker's to-do-tasks.md.
 */
export const PLATFORM_CAPABILITY_NOTES: Partial<Record<Platform, string>> = {
  Facebook:
    'Meta retired Page audience demographics (age, gender, country, city) with no replacement, '
    + 'and replaced organic impressions/reach with Views and Viewers. Figures from before and '
    + 'after that change are not directly comparable.',
  Instagram:
    'Instagram replaced impressions, plays and video views with a single Views metric.',
};
